import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Divider,
  Alert,
  CircularProgress,
  Button,
  Chip,
  Stack,
  TextField,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Download,
  Lock,
  Edit,
  Save,
  ExpandMore,
  CheckCircle,
  Email,
  ExpandLess,
  VisibilityOff,
  Flag,
  ChatBubbleOutline,
  Description,
  LocalHospital,
  Print,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { useThemeMode } from '../../contexts/ThemeModeContext';
import { useAuth } from '../../contexts/AuthContext';
import { formatPatientName } from '../../services/utils';
import {
  getBothReports,
  downloadReportPDF,
  printReportPDF,
  printClinicalReportViaIframe,
  updateReportWorkingCopy,
  facilityApproveReport,
  facilityFlagReport,
  facilityReplyReport
} from '../../services/reports';
import ReportStatusBadge from './ReportStatusBadge';
import DownloadOptionsDialog from './DownloadOptionsDialog';
import SendEmailDialog from '../Clinician/SendEmailDialog';

// ─── Helpers ────────────────────────────────────────────────────────────────

const ReportSection = ({ title, content, color = 'primary' }) => {
  if (!content) return null;
  return (
    <Box sx={{ mb: 1 }}>
      <Typography variant="caption" sx={{ fontWeight: 700, color, textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.72rem', display: 'block', mb: 0.25 }}>
        {title}
      </Typography>
      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.5, fontSize: '0.86rem' }}>
        {content}
      </Typography>
    </Box>
  );
};

// ─── Hospital Review Section ───────────────────────────────────────────────

const HospitalReviewSection = ({ report, study, onReportUpdated, onEditDycovueReport }) => {
  const { user: currentUser } = useAuth();
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';
  const [approving, setApproving] = useState(false);
  const [flagModalOpen, setFlagModalOpen] = useState(false);
  const [concernsText, setConcernsText] = useState('');
  const [flagging, setFlagging] = useState(false);
  const [flagError, setFlagError] = useState('');
  const [replyModalOpen, setReplyModalOpen] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [replying, setReplying] = useState(false);
  const [replyError, setReplyError] = useState('');
  const [actionAlert, setActionAlert] = useState(null);

  if (!report || !report._id) return null;

  const reviewStatus = report.facilityReview?.status || 'pending';
  const concerns = report.facilityReview?.concerns || '';
  const approvedBy = report.facilityReview?.approvedBy;
  const approvedAt = report.facilityReview?.approvedAt;
  const flaggedBy = report.facilityReview?.flaggedBy;
  const flaggedAt = report.facilityReview?.flaggedAt;

  const handleApprove = async () => {
    setApproving(true);
    setActionAlert(null);
    try {
      const res = await facilityApproveReport(report._id);
      setActionAlert({ severity: 'success', message: 'Report approved by hospital — all is calm.' });
      if (onReportUpdated && res.report) {
        onReportUpdated(res.report);
      }
    } catch (err) {
      console.error('Approve report error:', err);
      setActionAlert({
        severity: 'error',
        message: err.response?.data?.error || 'Failed to approve report'
      });
    } finally {
      setApproving(false);
    }
  };

  const handleOpenFlagModal = () => {
    setConcernsText(concerns || '');
    setFlagError('');
    setFlagModalOpen(true);
  };

  const handleSubmitFlag = async () => {
    if (!concernsText.trim()) {
      setFlagError('Please type your concerns regarding this report.');
      return;
    }
    setFlagging(true);
    setFlagError('');
    try {
      const res = await facilityFlagReport(report._id, concernsText.trim());
      setFlagModalOpen(false);
      setActionAlert({ severity: 'error', message: 'Report flagged with concerns. The radiologist has been alerted.' });
      if (onReportUpdated && res.report) {
        onReportUpdated(res.report);
      }
    } catch (err) {
      console.error('Flag report error:', err);
      setFlagError(err.response?.data?.error || 'Failed to flag report');
    } finally {
      setFlagging(false);
    }
  };

  const handleSubmitReply = async () => {
    if (!replyText.trim()) {
      setReplyError('Please enter your response to the concerns.');
      return;
    }
    setReplying(true);
    setReplyError('');
    try {
      const res = await facilityReplyReport(report._id, replyText.trim());
      setReplyModalOpen(false);
      setReplyText('');
      setActionAlert({ severity: 'success', message: 'Reply posted to report concerns thread.' });
      if (onReportUpdated && res.report) {
        onReportUpdated(res.report);
      }
    } catch (err) {
      console.error('Submit reply error:', err);
      setReplyError(err.response?.data?.error || 'Failed to submit reply');
    } finally {
      setReplying(false);
    }
  };

  return (
    <Box sx={{ mb: 2.5 }}>
      {actionAlert && (
        <Alert
          severity={actionAlert.severity}
          onClose={() => setActionAlert(null)}
          sx={{ mb: 1.5, borderRadius: '8px' }}
        >
          {actionAlert.message}
        </Alert>
      )}

      {reviewStatus === 'approved' ? (
        <Box
          sx={{
            p: 1.75,
            borderRadius: '10px',
            bgcolor: isDark ? alpha('#10B981', 0.12) : alpha('#10B981', 0.08),
            border: '1px solid',
            borderColor: isDark ? alpha('#10B981', 0.3) : alpha('#10B981', 0.25),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1.5,
          }}
        >
          <Box display="flex" alignItems="center" gap={1.25}>
            <CheckCircle sx={{ color: '#10B981', fontSize: 24 }} />
            <Box>
              <Typography variant="subtitle2" sx={{ color: isDark ? '#34D399' : '#065F46', fontWeight: 700 }}>
                Hospital Review: Approved (All Clear)
              </Typography>
              <Typography variant="caption" sx={{ color: isDark ? '#A7F3D0' : '#047857', display: 'block' }}>
                {approvedBy?.firstName
                  ? `Approved by ${approvedBy.firstName} ${approvedBy.lastName || ''} on ${new Date(approvedAt).toLocaleString()}`
                  : approvedAt
                  ? `Approved on ${new Date(approvedAt).toLocaleString()}`
                  : 'Report accepted by facility — all is calm.'}
              </Typography>
            </Box>
          </Box>
          <Button
            size="small"
            variant="outlined"
            color="error"
            startIcon={<Flag sx={{ fontSize: 16 }} />}
            onClick={handleOpenFlagModal}
            sx={{ textTransform: 'none', borderRadius: '8px', fontSize: '0.75rem', fontWeight: 600 }}
          >
            Flag Concerns
          </Button>
        </Box>
      ) : reviewStatus === 'flagged' ? (
        <Box
          sx={{
            p: 2,
            borderRadius: '10px',
            bgcolor: isDark ? alpha('#EF4444', 0.12) : alpha('#EF4444', 0.08),
            border: '1px solid',
            borderColor: isDark ? alpha('#EF4444', 0.35) : alpha('#EF4444', 0.25),
          }}
        >
          <Box display="flex" alignItems="flex-start" justifyContent="space-between" flexWrap="wrap" gap={1.5}>
            <Box display="flex" alignItems="flex-start" gap={1.25} sx={{ flex: 1, minWidth: 260 }}>
              <Flag sx={{ color: '#EF4444', fontSize: 24, mt: 0.25 }} />
              <Box sx={{ width: '100%' }}>
                <Typography variant="subtitle2" sx={{ color: isDark ? '#F87171' : '#B91C1C', fontWeight: 700 }}>
                  Report Flagged with Concerns
                </Typography>
                <Typography variant="caption" sx={{ color: isDark ? '#FECACA' : '#991B1B', display: 'block', mb: 1 }}>
                  {flaggedBy?.firstName
                    ? `Flagged by ${flaggedBy.firstName} ${flaggedBy.lastName || ''} on ${new Date(flaggedAt).toLocaleString()}`
                    : flaggedAt
                    ? `Flagged on ${new Date(flaggedAt).toLocaleString()}`
                    : 'Hospital concerns raised'}
                </Typography>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    borderRadius: '8px',
                    bgcolor: isDark ? '#1E1E1E' : '#FFFFFF',
                    borderColor: isDark ? alpha('#EF4444', 0.3) : alpha('#EF4444', 0.2),
                  }}
                >
                  <Typography variant="caption" color="textSecondary" display="block" fontWeight={600} mb={0.5}>
                    Hospital Concerns / Feedback:
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: isDark ? '#F1F5F9' : '#1F2937' }}>
                    {concerns}
                  </Typography>
                </Paper>

                {/* Radiologist Response / Thread tied to this specific report */}
                {Boolean(report.facilityReview?.radiologistReply || (report.facilityReview?.replies && report.facilityReview.replies.length > 0)) && (
                  <Box sx={{ mt: 1.25 }}>
                    <Typography variant="caption" sx={{ color: isDark ? '#93C5FD' : '#1D4ED8', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                      <ChatBubbleOutline sx={{ fontSize: 13 }} />
                      In-House Radiologist Reply:
                    </Typography>
                    {(report.facilityReview?.replies && report.facilityReview.replies.length > 0) ? (
                      <Stack spacing={0.75}>
                        {report.facilityReview.replies.map((rep, idx) => (
                          <Paper
                            key={idx}
                            variant="outlined"
                            sx={{
                              p: 1.25,
                              borderRadius: '8px',
                              bgcolor: isDark ? 'rgba(37, 99, 235, 0.12)' : '#EFF6FF',
                              borderColor: isDark ? alpha('#3B82F6', 0.35) : alpha('#3B82F6', 0.25),
                            }}
                          >
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.25}>
                              <Typography variant="caption" sx={{ fontWeight: 700, color: isDark ? '#BFDBFE' : '#1E40AF' }}>
                                {rep.user?.firstName ? `Dr. ${rep.user.firstName} ${rep.user.lastName || ''}` : 'In-House Radiologist'}
                              </Typography>
                              {rep.createdAt && (
                                <Typography variant="caption" sx={{ color: isDark ? '#93C5FD' : '#60A5FA', fontSize: '0.68rem' }}>
                                  {new Date(rep.createdAt).toLocaleString()}
                                </Typography>
                              )}
                            </Box>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: isDark ? '#F1F5F9' : '#1F2937', fontSize: '0.82rem' }}>
                              {rep.message}
                            </Typography>
                          </Paper>
                        ))}
                      </Stack>
                    ) : (
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 1.25,
                          borderRadius: '8px',
                          bgcolor: isDark ? 'rgba(37, 99, 235, 0.12)' : '#EFF6FF',
                          borderColor: isDark ? alpha('#3B82F6', 0.35) : alpha('#3B82F6', 0.25),
                        }}
                      >
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.25}>
                          <Typography variant="caption" sx={{ fontWeight: 700, color: isDark ? '#BFDBFE' : '#1E40AF' }}>
                            {report.facilityReview?.repliedBy?.firstName
                              ? `Dr. ${report.facilityReview.repliedBy.firstName} ${report.facilityReview.repliedBy.lastName || ''}`
                              : 'In-House Radiologist'}
                          </Typography>
                          {report.facilityReview?.repliedAt && (
                            <Typography variant="caption" sx={{ color: isDark ? '#93C5FD' : '#60A5FA', fontSize: '0.68rem' }}>
                              {new Date(report.facilityReview.repliedAt).toLocaleString()}
                            </Typography>
                          )}
                        </Box>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: isDark ? '#F1F5F9' : '#1F2937', fontSize: '0.82rem' }}>
                          {report.facilityReview?.radiologistReply}
                        </Typography>
                      </Paper>
                    )}
                  </Box>
                )}
              </Box>
            </Box>
            <Stack direction="row" spacing={1} sx={{ mt: { xs: 1, sm: 0 }, alignSelf: { sm: 'flex-start' } }}>
              {(() => {
                const currentUserId = currentUser?._id?.toString();
                const flaggerId = (flaggedBy?._id || flaggedBy)?.toString();
                const radiologistUser = report.createdBy || report.assignedRadiologist || report.study?.assignedRadiologist;
                const radiologistId = (radiologistUser?._id || radiologistUser)?.toString();

                const isCurrentUserFlagger = Boolean(currentUserId && flaggerId && currentUserId === flaggerId);

                let targetUser = null;
                let buttonText = 'Message Reviewer';

                if (isCurrentUserFlagger) {
                  targetUser = typeof radiologistUser === 'object' && radiologistUser !== null
                    ? radiologistUser
                    : (radiologistId ? { _id: radiologistId, firstName: 'Radiologist', lastName: '' } : null);
                  buttonText = 'Message Radiologist';
                } else {
                  targetUser = typeof flaggedBy === 'object' && flaggedBy !== null
                    ? flaggedBy
                    : (flaggerId ? { _id: flaggerId, firstName: 'Reviewer', lastName: '' } : null);
                  buttonText = 'Message Reviewer';
                }

                // Safety fallback: never target current user
                if (targetUser?._id && currentUserId && targetUser._id.toString() === currentUserId) {
                  if (radiologistId && radiologistId !== currentUserId) {
                    targetUser = typeof radiologistUser === 'object' ? radiologistUser : { _id: radiologistId, firstName: 'Radiologist', lastName: '' };
                    buttonText = 'Message Radiologist';
                  } else if (flaggerId && flaggerId !== currentUserId) {
                    targetUser = typeof flaggedBy === 'object' ? flaggedBy : { _id: flaggerId, firstName: 'Reviewer', lastName: '' };
                    buttonText = 'Message Reviewer';
                  } else {
                    targetUser = null;
                  }
                }

                if (!targetUser || !targetUser._id || (currentUserId && targetUser._id.toString() === currentUserId)) {
                  return null;
                }

                return (
                  <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    startIcon={<ChatBubbleOutline sx={{ fontSize: 16 }} />}
                    onClick={() => {
                      window.dispatchEvent(
                        new CustomEvent('open-direct-message', {
                          detail: {
                            user: targetUser,
                            context: {
                              type: 'flagged_report',
                              reportId: report._id,
                              studyId: report.study?._id || report.study,
                              patientName: report.patientName || report.study?.patientName,
                              modality: report.modality || report.study?.modality,
                              concerns: concerns
                            }
                          }
                        })
                      );
                    }}
                    sx={{
                      textTransform: 'none',
                      borderRadius: '8px',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      bgcolor: '#2563EB',
                      '&:hover': { bgcolor: '#1D4ED8' }
                    }}
                  >
                    {buttonText}
                  </Button>
                );
              })()}
              <Button
                size="small"
                variant="contained"
                color="primary"
                startIcon={<ChatBubbleOutline sx={{ fontSize: 16 }} />}
                onClick={() => {
                  setReplyText('');
                  setReplyError('');
                  setReplyModalOpen(true);
                }}
                sx={{
                  textTransform: 'none',
                  borderRadius: '8px',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  bgcolor: '#2563EB',
                  '&:hover': { bgcolor: '#1D4ED8' }
                }}
              >
                Reply to Concerns
              </Button>
              {onEditDycovueReport && (
                <Button
                  size="small"
                  variant="contained"
                  color="warning"
                  startIcon={<Edit sx={{ fontSize: 16 }} />}
                  onClick={() => onEditDycovueReport(study)}
                  sx={{
                    textTransform: 'none',
                    borderRadius: '8px',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    bgcolor: '#EA580C',
                    '&:hover': { bgcolor: '#C2410C' }
                  }}
                >
                  Revise Report Form
                </Button>
              )}
              <Button
                size="small"
                variant="contained"
                color="success"
                startIcon={approving ? <CircularProgress size={14} color="inherit" /> : <CheckCircle sx={{ fontSize: 16 }} />}
                disabled={approving}
                onClick={handleApprove}
                sx={{ textTransform: 'none', borderRadius: '8px', fontSize: '0.75rem', fontWeight: 600 }}
              >
                Approve (Resolve)
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="inherit"
                startIcon={<Edit sx={{ fontSize: 16 }} />}
                onClick={handleOpenFlagModal}
                sx={{ textTransform: 'none', borderRadius: '8px', fontSize: '0.75rem', fontWeight: 600 }}
              >
                Edit Concerns
              </Button>
            </Stack>
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            p: 2,
            borderRadius: '10px',
            bgcolor: isDark ? alpha('#3B82F6', 0.08) : alpha('#3B82F6', 0.04),
            border: '1px dashed',
            borderColor: isDark ? alpha('#3B82F6', 0.3) : alpha('#3B82F6', 0.2),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1.5,
          }}
        >
          <Box>
            <Typography variant="subtitle2" fontWeight={700} color="textPrimary">
              Hospital Review &amp; Quality Check
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Review this in-house radiologist report. Approve if all is calm, or flag concerns to request clarification.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="contained"
              color="success"
              startIcon={approving ? <CircularProgress size={14} color="inherit" /> : <CheckCircle sx={{ fontSize: 16 }} />}
              disabled={approving}
              onClick={handleApprove}
              sx={{
                textTransform: 'none',
                borderRadius: '8px',
                fontSize: '0.8rem',
                fontWeight: 600,
                px: 2,
                boxShadow: 'none',
                '&:hover': { boxShadow: '0 2px 8px rgba(16,185,129,0.3)' }
              }}
            >
              Approve Report
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="error"
              startIcon={<Flag sx={{ fontSize: 16 }} />}
              onClick={handleOpenFlagModal}
              sx={{
                textTransform: 'none',
                borderRadius: '8px',
                fontSize: '0.8rem',
                fontWeight: 600,
                px: 1.75,
              }}
            >
              Flag Concerns
            </Button>
          </Stack>
        </Box>
      )}

      {/* Flag Report Concerns Modal */}
      <Dialog
        open={flagModalOpen}
        onClose={() => !flagging && setFlagModalOpen(false)}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: { xs: '95vw', sm: '520px' },
            maxWidth: '540px !important',
            maxHeight: '85vh',
            borderRadius: '14px',
            p: 1
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <Flag color="error" />
            <Typography variant="h6" fontWeight={700}>
              Flag Report Concerns
            </Typography>
          </Box>
          <Typography variant="caption" color="textSecondary" display="block" mt={0.5}>
            Provide details of any concerns, discrepancies, or revision requests for the reporting radiologist.
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          {flagError && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: '8px' }}>
              {flagError}
            </Alert>
          )}
          <TextField
            label="Your Concerns &amp; Feedback"
            placeholder="Type your concerns here (e.g. Findings discrepancy, anatomical question, missing recommendation)..."
            multiline
            rows={4}
            fullWidth
            value={concernsText}
            onChange={(e) => setConcernsText(e.target.value)}
            disabled={flagging}
            autoFocus
            required
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setFlagModalOpen(false)}
            disabled={flagging}
            color="inherit"
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={flagging ? <CircularProgress size={16} color="inherit" /> : <Flag />}
            disabled={flagging || !concernsText.trim()}
            onClick={handleSubmitFlag}
            sx={{ textTransform: 'none', borderRadius: '8px', fontWeight: 600 }}
          >
            {flagging ? 'Submitting...' : 'Submit Flag'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reply to Concerns Dialog */}
      <Dialog
        open={replyModalOpen}
        onClose={() => !replying && setReplyModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            bgcolor: isDark ? '#1E1E1E' : '#FFFFFF',
            border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid #E2E8F0',
          }
        }}
      >
        <DialogTitle sx={{ pb: 1, borderBottom: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid #F1F5F9' }}>
          <Box display="flex" alignItems="center" gap={1}>
            <ChatBubbleOutline sx={{ color: '#2563EB', fontSize: 20 }} />
            <Typography variant="subtitle1" fontWeight={700}>
              Reply to Hospital Concerns
            </Typography>
          </Box>
          <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
            This reply is attached directly to this specific report so all reviewers and clinicians can immediately see your clarification.
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {replyError && (
            <Alert severity="error" sx={{ mb: 1.5 }}>
              {replyError}
            </Alert>
          )}
          <Paper
            variant="outlined"
            sx={{
              p: 1.25,
              mb: 1.5,
              borderRadius: '8px',
              bgcolor: isDark ? '#121212' : '#FEF2F2',
              borderColor: isDark ? alpha('#EF4444', 0.3) : alpha('#EF4444', 0.25),
            }}
          >
            <Typography variant="caption" color="textSecondary" fontWeight={600} display="block">
              Hospital Concerns:
            </Typography>
            <Typography variant="body2" sx={{ color: isDark ? '#FCA5A5' : '#B91C1C', fontSize: '0.8rem' }}>
              {concerns || 'No details specified.'}
            </Typography>
          </Paper>
          <TextField
            multiline
            rows={4}
            fullWidth
            size="small"
            label="Radiologist Reply / Clarification"
            placeholder="Explain any findings, technique details, or revisions made to address this concern..."
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            disabled={replying}
            autoFocus
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid #F1F5F9' }}>
          <Button onClick={() => setReplyModalOpen(false)} disabled={replying} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmitReply}
            disabled={replying || !replyText.trim()}
            startIcon={replying ? <CircularProgress size={14} color="inherit" /> : <Save sx={{ fontSize: 16 }} />}
            sx={{
              textTransform: 'none',
              bgcolor: '#2563EB',
              '&:hover': { bgcolor: '#1D4ED8' }
            }}
          >
            {replying ? 'Posting...' : 'Post Reply to Report'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ─── Original Report Card (read-only) ───────────────────────────────────────

const OriginalReportCard = ({ report, type, study, hasWorkingCopy, onReportUpdated, onEditDycovueReport }) => {
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';
  const [downloading, setDownloading] = useState(false);
  const [showDownloadDialog, setShowDownloadDialog] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [printing, setPrinting] = useState(false);

  const isApproved = report && ['approved', 'amended'].includes(report.status);
  const color = type === 'self-report' ? 'primary' : 'secondary';
  const title = type === 'self-report'
    ? 'Self-Report — Original'
    : 'DycoVue Report — Original';

  const handleDownloadPDF = async (options = {}) => {
    setDownloading(true);
    try {
      await downloadReportPDF(report._id, options);
    } catch (err) {
      console.error('Download error:', err);
    } finally {
      setDownloading(false);
    }
  };

  const handlePrint = async () => {
    setPrinting(true);
    try {
      await printReportPDF(report._id, { report, study, type });
    } catch (err) {
      console.warn('Print PDF error, triggering isolated client-side report print:', err);
      printClinicalReportViaIframe(report, study, type);
    } finally {
      setPrinting(false);
    }
  };

  if (!report) {
    return (
      <Alert severity="info" sx={{ my: 1 }}>
        <Typography variant="body2">
          {type === 'self-report'
            ? 'No self-report has been created for this study.'
            : 'DycoVue report has not been requested or is not yet available.'}
        </Typography>
      </Alert>
    );
  }

  return (
    <Paper 
      variant="outlined" 
      sx={{ 
        p: { xs: 1.5, sm: 2 }, 
        borderRadius: '10px',
        bgcolor: isDark ? 'rgba(255,255,255,0.02)' : '#FFFFFF',
        borderColor: isDark ? 'rgba(255,255,255,0.08)' : '#E2E8F0',
      }}
    >
      {/* Header */}
      <Box sx={{ mb: 1.5 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <Lock fontSize="small" sx={{ color: 'text.secondary', fontSize: 16 }} />
            <Typography variant="subtitle1" fontWeight={700} color={color} sx={{ fontSize: '0.95rem' }}>
              {title}
            </Typography>
            <Chip label={`v${report.version || 1}`} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.68rem', fontWeight: 600 }} />
            <Chip label={report.modality} size="small" color="info" variant="outlined" sx={{ height: 20, fontSize: '0.68rem', fontWeight: 600 }} />
            {report.createdBy && (
              <Chip
                label={`By: ${report.createdBy.firstName} ${report.createdBy.lastName}`}
                size="small"
                variant="outlined"
                sx={{ height: 20, fontSize: '0.68rem' }}
              />
            )}
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            {type === 'dycovue-report' && report?.facilityReview?.status === 'approved' && (
              <Chip
                label="Hospital Accepted"
                color="success"
                size="small"
                icon={<CheckCircle sx={{ fontSize: 12 }} />}
                sx={{ height: 20, fontSize: '0.68rem', fontWeight: 700 }}
              />
            )}
            {type === 'dycovue-report' && report?.facilityReview?.status === 'flagged' && (
              <Chip
                label="Concerns Flagged"
                color="error"
                size="small"
                icon={<Flag sx={{ fontSize: 12 }} />}
                sx={{ height: 20, fontSize: '0.68rem', fontWeight: 700 }}
              />
            )}
            <ReportStatusBadge
              status={report.status}
              type={type}
              isFlagged={report?.facilityReview?.status === 'flagged'}
            />
          </Box>
        </Box>

        {isApproved && report.approvedAt && (
          <Typography variant="caption" color="textSecondary" display="block" mt={0.5} sx={{ fontSize: '0.72rem' }}>
            Approved on {new Date(report.approvedAt).toLocaleString()}
          </Typography>
        )}
      </Box>

      <Divider sx={{ mb: 1.5 }} />

      {/* Content */}
      <Box>
        <Grid container spacing={1.5}>
          {report.clinicalHistory && (
            <Grid item xs={12} sm={report.technique ? 6 : 12}>
              <ReportSection title="Clinical History" content={report.clinicalHistory} color={color} />
            </Grid>
          )}
          {report.technique && (
            <Grid item xs={12} sm={report.clinicalHistory ? 6 : 12}>
              <ReportSection title="Technique" content={report.technique} color={color} />
            </Grid>
          )}
          {report.comparison && (
            <Grid item xs={12}>
              <ReportSection title="Comparison" content={report.comparison} color={color} />
            </Grid>
          )}
          <Grid item xs={12}>
            <ReportSection title="Findings" content={report.findings} color={color} />
          </Grid>
          <Grid item xs={12}>
            <ReportSection title="Impression" content={report.impression} color={color} />
          </Grid>
          {report.recommendations && (
            <Grid item xs={12}>
              <ReportSection title="Recommendations" content={report.recommendations} color={color} />
            </Grid>
          )}
        </Grid>

        {/* Modality-Specific Fields */}
        {report.modalityFields && Object.keys(report.modalityFields).length > 0 && (
          <Box sx={{ mt: 1, mb: 1 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color, textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.72rem', display: 'block', mb: 0.5 }}>
              {report.modality} Specific Findings
            </Typography>
            <Paper variant="outlined" sx={{ p: 1, bgcolor: isDark ? 'rgba(255,255,255,0.03)' : '#F8FAFC' }}>
              <Grid container spacing={1}>
                {Object.entries(report.modalityFields).map(([key, value]) => (
                  <Grid item xs={12} sm={6} key={key}>
                    <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 600 }}>
                      {key.replace(/([A-Z])/g, ' $1').trim()}:
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.82rem' }}>
                      {typeof value === 'object' ? JSON.stringify(value) : value}
                    </Typography>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          </Box>
        )}

        {/* Critical Findings Alert */}
        {report.criticalFindings?.present && (
          <Alert severity="error" sx={{ mt: 1.5, py: 0.5 }}>
            <Typography variant="body2" fontWeight="bold">CRITICAL FINDING</Typography>
            <Typography variant="body2">{report.criticalFindings.description}</Typography>
          </Alert>
        )}
      </Box>

      {/* Hospital Review Section (moved under report content) */}
      {type === 'dycovue-report' && (
        <Box sx={{ mt: 2 }}>
          <HospitalReviewSection report={report} study={study} onReportUpdated={onReportUpdated} onEditDycovueReport={onEditDycovueReport} />
        </Box>
      )}

      {/* Actions */}
      <Box sx={{ mt: 2, pt: 1.5, borderTop: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.72rem', display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Lock sx={{ fontSize: 13 }} /> Locked original copy.
        </Typography>
        {isApproved && (
          <Stack direction="row" spacing={1}>
            {type === 'dycovue-report' && onEditDycovueReport && (
              <Button
                size="small"
                color={report?.facilityReview?.status === 'flagged' ? "error" : "primary"}
                variant="contained"
                startIcon={report?.facilityReview?.status === 'flagged' ? <Flag sx={{ fontSize: 16 }} /> : <Edit sx={{ fontSize: 16 }} />}
                onClick={() => onEditDycovueReport(study)}
                sx={{ textTransform: 'none', borderRadius: '6px', fontSize: '0.78rem', py: 0.5, px: 1.5, fontWeight: 700 }}
              >
                {report?.facilityReview?.status === 'flagged' ? 'Review & Revise Flagged Report' : 'Edit In Report Form'}
              </Button>
            )}
            <Button
              size="small"
              startIcon={downloading ? <CircularProgress size={14} /> : <Download sx={{ fontSize: 16 }} />}
              onClick={() => setShowDownloadDialog(true)}
              disabled={downloading}
              variant="outlined"
              sx={{ textTransform: 'none', borderRadius: '6px', fontSize: '0.78rem', py: 0.5, px: 1.5 }}
            >
              Download PDF
            </Button>
            <Button
              size="small"
              startIcon={printing ? <CircularProgress size={14} /> : <Print sx={{ fontSize: 16 }} />}
              onClick={handlePrint}
              disabled={printing}
              variant="outlined"
              sx={{ textTransform: 'none', borderRadius: '6px', fontSize: '0.78rem', py: 0.5, px: 1.5 }}
            >
              Print
            </Button>
            <Button
              size="small"
              startIcon={<Email sx={{ fontSize: 16 }} />}
              onClick={() => setShowShareDialog(true)}
              variant="outlined"
              color="secondary"
              sx={{ textTransform: 'none', borderRadius: '6px', fontSize: '0.78rem', py: 0.5, px: 1.5 }}
            >
              Share
            </Button>
          </Stack>
        )}

        <DownloadOptionsDialog
          open={showDownloadDialog}
          onClose={() => setShowDownloadDialog(false)}
          onDownload={handleDownloadPDF}
          reportType={type}
        />
        <SendEmailDialog
          open={showShareDialog}
          onClose={() => setShowShareDialog(false)}
          study={study}
          defaultReportVersion="original"
          hasWorkingCopies={!!hasWorkingCopy}
        />
      </Box>
    </Paper>
  );
};

// ─── Working Copy Card (editable) ───────────────────────────────────────────

const EDITABLE_FIELDS = [
  { key: 'clinicalHistory', label: 'Clinical History' },
  { key: 'technique', label: 'Technique' },
  { key: 'comparison', label: 'Comparison' },
  { key: 'findings', label: 'Findings' },
  { key: 'impression', label: 'Impression' },
  { key: 'recommendations', label: 'Recommendations' }
];

const WorkingCopyCard = ({ report, type, study, onSaved, onReportUpdated, onEditDycovueReport }) => {
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saved, setSaved] = useState(false);
  const [showDownloadDialog, setShowDownloadDialog] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);

  const color = type === 'self-report' ? 'primary' : 'secondary';
  const title = type === 'self-report'
    ? 'Self-Report — Editable Version'
    : 'DycoVue Report — Editable Version';

  useEffect(() => {
    if (report) {
      const initial = {};
      EDITABLE_FIELDS.forEach(({ key }) => {
        initial[key] = report[key] || '';
      });
      setFormData(initial);
    }
  }, [report]);

  const handleChange = (key) => (e) => {
    setFormData((prev) => ({ ...prev, [key]: e.target.value }));
    setSaved(false);
    setSaveError('');
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError('');
    setSaved(false);
    try {
      const updated = await updateReportWorkingCopy(report._id, type, formData);
      setSaved(true);
      if (onSaved) onSaved(updated, type);
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.errors?.[0]?.msg ||
        err.message ||
        'Failed to save';
      setSaveError(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadPDF = async (options = {}) => {
    setDownloading(true);
    try {
      await downloadReportPDF(report._id, options);
    } catch (err) {
      console.error('Download error:', err);
    } finally {
      setDownloading(false);
    }
  };

  const [printing, setPrinting] = useState(false);
  const handlePrint = async () => {
    setPrinting(true);
    try {
      await printReportPDF(report._id, { report, study, type: 'working-copy' });
    } catch (err) {
      console.warn('Print PDF error, triggering isolated client-side report print:', err);
      printClinicalReportViaIframe(report, study, 'working-copy');
    } finally {
      setPrinting(false);
    }
  };

  if (!report) {
    return (
      <Alert severity="info">
        <Typography variant="body2" fontWeight={600} gutterBottom>
          No editable version yet
        </Typography>
        <Typography variant="body2">
          An editable version is created automatically the first time the original report is approved.
          Once created, you can update it as many times as needed without affecting the locked original.
        </Typography>
      </Alert>
    );
  }

  return (
    <Paper sx={{ p: 3, border: 2, borderColor: `${color}.light`, borderStyle: 'dashed' }}>
      {/* Header */}
      <Box sx={{ mb: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Box display="flex" alignItems="center" gap={1}>
            <Edit fontSize="small" color={color} />
            <Typography variant="h6" color={color}>
              {title}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            {type === 'dycovue-report' && report?.facilityReview?.status === 'approved' && (
              <Chip
                label="Hospital Accepted"
                color="success"
                size="small"
                icon={<CheckCircle sx={{ fontSize: 13 }} />}
                sx={{ height: 22, fontSize: '0.7rem', fontWeight: 700 }}
              />
            )}
            {type === 'dycovue-report' && report?.facilityReview?.status === 'flagged' && (
              <Chip
                label="Concerns Flagged"
                color="error"
                size="small"
                icon={<Flag sx={{ fontSize: 13 }} />}
                sx={{ height: 22, fontSize: '0.7rem', fontWeight: 700 }}
              />
            )}
            {report?.facilityReview?.status === 'flagged' ? (
              <Chip label="Revision Needed" size="small" color="error" variant="filled" sx={{ fontWeight: 700 }} />
            ) : (
              <Chip label="Editable" size="small" color="warning" variant="filled" />
            )}
          </Box>
        </Box>

        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Chip label={`v${report.version}`} size="small" variant="outlined" />
          <Chip label={report.modality} size="small" color="info" variant="outlined" />
        </Stack>

        {report.updatedAt && (
          <Typography variant="caption" color="textSecondary" display="block" mt={1}>
            Last saved: {new Date(report.updatedAt).toLocaleString()}
          </Typography>
        )}
      </Box>

      <Divider sx={{ mb: 2 }} />

      {/* Hospital Review Section (for in-house DycoVue report) */}
      {type === 'dycovue-report' && (
        <HospitalReviewSection report={report} study={study} onReportUpdated={onReportUpdated} onEditDycovueReport={onEditDycovueReport} />
      )}

      {/* Editable Fields */}
      <Stack spacing={2}>
        {EDITABLE_FIELDS.map(({ key, label }) => (
          <TextField
            key={key}
            label={label}
            multiline
            minRows={key === 'findings' || key === 'impression' ? 4 : 2}
            fullWidth
            size="small"
            value={formData[key] || ''}
            onChange={handleChange(key)}
            color={color}
          />
        ))}
      </Stack>

      {/* Actions */}
      <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
        {saveError && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {saveError}
          </Alert>
        )}
        {saved && (
          <Alert severity="success" icon={<CheckCircle fontSize="small" />} sx={{ mb: 1 }}>
            Editable version saved successfully.
          </Alert>
        )}

        <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
          <Button
            variant="contained"
            color={color}
            startIcon={saving ? <CircularProgress size={16} /> : <Save />}
            disabled={saving}
            onClick={handleSave}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
          <Button
            size="small"
            startIcon={downloading ? <CircularProgress size={16} /> : <Download />}
            onClick={() => setShowDownloadDialog(true)}
            disabled={downloading}
            variant="outlined"
          >
            Download PDF
          </Button>
          <Button
            size="small"
            startIcon={printing ? <CircularProgress size={16} /> : <Print />}
            onClick={handlePrint}
            disabled={printing}
            variant="outlined"
          >
            Print
          </Button>
          <Button
            size="small"
            startIcon={<Email />}
            onClick={() => setShowShareDialog(true)}
            variant="outlined"
            color="secondary"
          >
            Share
          </Button>
        </Stack>

        <DownloadOptionsDialog
          open={showDownloadDialog}
          onClose={() => setShowDownloadDialog(false)}
          onDownload={handleDownloadPDF}
          reportType={type}
        />
        <SendEmailDialog
          open={showShareDialog}
          onClose={() => setShowShareDialog(false)}
          study={study}
          defaultReportVersion="working"
          hasWorkingCopies={true}
        />
      </Box>
    </Paper>
  );
};

// ─── Report Type Section ─────────────────────────────────────────────────────

const ReportTypeSection = ({ label, original, workingCopy, type, study, onWorkingCopySaved, onReportUpdated, onEditDycovueReport }) => {
  const { user: currentUser } = useAuth();
  const isRadiologist = currentUser?.role === 'radiologist' || currentUser?.role === 'in_house_radiologist' || currentUser?.role === 'admin';
  const canEditThisReport = type === 'self-report' ? true : isRadiologist;
  const hasWorkingCopy = !!workingCopy;
  const [showEditable, setShowEditable] = useState(false);
  const hasOriginal = !!original;

  if (!hasOriginal) {
    return (
      <Alert severity="info" sx={{ my: 1.5 }}>
        <Typography variant="body2">
          {type === 'self-report'
            ? 'No self-report has been created for this study.'
            : 'DycoVue report has not been requested or is not yet available.'}
        </Typography>
      </Alert>
    );
  }

  return (
    <Box>
      {/* Original report — always visible */}
      <OriginalReportCard
        report={original}
        type={type}
        study={study}
        hasWorkingCopy={hasWorkingCopy}
        onReportUpdated={onReportUpdated}
        onEditDycovueReport={onEditDycovueReport}
      />

      {/* Toggle button for editable version - restricted to authorized roles */}
      {canEditThisReport && (
        <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            size="small"
            variant={showEditable ? 'outlined' : 'text'}
            color={type === 'self-report' ? 'primary' : 'secondary'}
            startIcon={showEditable ? <VisibilityOff sx={{ fontSize: 16 }} /> : <Edit sx={{ fontSize: 16 }} />}
            endIcon={showEditable ? <ExpandLess sx={{ fontSize: 16 }} /> : <ExpandMore sx={{ fontSize: 16 }} />}
            onClick={() => setShowEditable((v) => !v)}
            sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.78rem', py: 0.25 }}
          >
            {showEditable ? 'Hide Editable Version' : 'View / Edit Working Copy'}
          </Button>
        </Box>
      )}

      {canEditThisReport && (
        <Collapse in={showEditable} timeout="auto" unmountOnExit={false}>
          <Box sx={{ mt: 1 }}>
            <WorkingCopyCard
              report={workingCopy}
              type={type}
              study={study}
              onSaved={onWorkingCopySaved}
              onReportUpdated={onReportUpdated}
              onEditDycovueReport={onEditDycovueReport}
            />
          </Box>
        </Collapse>
      )}
    </Box>
  );
};

// ─── Main ReportViewer ───────────────────────────────────────────────────────

const ReportViewer = ({ studyId, study, reportType = 'all', onReportTypeChange, onEditDycovueReport }) => {
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';
  const [reports, setReports] = useState({
    selfReport: null,
    selfReportWorkingCopy: null,
    dycovueReport: null,
    dycovueReportWorkingCopy: null
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Active tab to cleanly separate Self-Report and In-House Report
  const [activeTab, setActiveTab] = useState(() => {
    if (reportType === 'dycovue-report') return 'dycovue-report';
    return 'self-report';
  });

  useEffect(() => {
    if (reportType === 'dycovue-report' || reportType === 'self-report') {
      setActiveTab(reportType);
    }
  }, [reportType]);

  useEffect(() => {
    const loadReports = async () => {
      if (!studyId) return;
      setLoading(true);
      setError('');
      try {
        const data = await getBothReports(studyId);
        setReports({
          selfReport: data.selfReport,
          selfReportWorkingCopy: data.selfReportWorkingCopy,
          dycovueReport: data.dycovueReport,
          dycovueReportWorkingCopy: data.dycovueReportWorkingCopy
        });
      } catch (err) {
        console.error('Load reports error:', err);
        setError('Failed to load reports');
      } finally {
        setLoading(false);
      }
    };

    loadReports();
  }, [studyId]);

  const handleWorkingCopySaved = (updatedReport, type) => {
    if (type === 'self-report') {
      setReports((prev) => ({ ...prev, selfReportWorkingCopy: updatedReport }));
    } else {
      setReports((prev) => ({ ...prev, dycovueReportWorkingCopy: updatedReport }));
    }
  };

  const handleReportUpdated = (updatedReport, reportType) => {
    if (reportType === 'self-report') {
      setReports((prev) => ({
        ...prev,
        selfReport: updatedReport.versionType === 'original' || !updatedReport.versionType ? updatedReport : prev.selfReport,
        selfReportWorkingCopy: updatedReport.versionType === 'working' ? updatedReport : prev.selfReportWorkingCopy,
      }));
    } else {
      setReports((prev) => ({
        ...prev,
        dycovueReport: updatedReport.versionType === 'original' || !updatedReport.versionType ? updatedReport : {
          ...(prev.dycovueReport || {}),
          facilityReview: updatedReport.facilityReview,
        },
        dycovueReportWorkingCopy: updatedReport.versionType === 'working' ? updatedReport : (prev.dycovueReportWorkingCopy ? {
          ...prev.dycovueReportWorkingCopy,
          facilityReview: updatedReport.facilityReview,
        } : null),
      }));
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      {/* Study Summary - Compact Single-Row Banner */}
      <Box
        sx={{
          px: 1.5,
          py: 0.75,
          mb: 1.25,
          borderRadius: '8px',
          bgcolor: isDark ? 'rgba(255,255,255,0.04)' : '#F8FAFC',
          border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#E2E8F0'}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
            <Box component="span" sx={{ color: 'text.secondary', fontWeight: 500 }}>Patient: </Box>
            <strong>{formatPatientName(study?.patientName)}</strong>
          </Typography>
          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
            <Box component="span" sx={{ color: 'text.secondary', fontWeight: 500 }}>Study: </Box>
            <strong>{study?.modality} — {study?.studyDescription || 'No description'}</strong>
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
          Date: <strong>{study?.studyDate ? new Date(study.studyDate).toLocaleDateString() : 'Unknown'}</strong>
        </Typography>
      </Box>

      {/* Tabs to cleanly separate Self-Report and In-House Report */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 1.25 }}>
        <Tabs
          value={activeTab}
          onChange={(e, val) => {
            setActiveTab(val);
            if (onReportTypeChange) onReportTypeChange(val);
          }}
          textColor="primary"
          indicatorColor="primary"
          sx={{
            minHeight: 36,
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '0.82rem',
              minHeight: 36,
              py: 0.5,
              px: 2,
            }
          }}
        >
          <Tab
            value="self-report"
            label={
              <Box display="flex" alignItems="center" gap={0.75}>
                <Description sx={{ fontSize: '0.95rem', color: '#0EA5E9' }} />
                <span>Self-Report (Clinician)</span>
                {reports.selfReport && (
                  <Chip
                    label={reports.selfReport.status || 'Created'}
                    size="small"
                    color={reports.selfReport.status === 'approved' ? 'success' : 'default'}
                    sx={{ height: 18, fontSize: '0.62rem', fontWeight: 700 }}
                  />
                )}
              </Box>
            }
          />
          <Tab
            value="dycovue-report"
            label={
              <Box display="flex" alignItems="center" gap={0.75}>
                <LocalHospital sx={{ fontSize: '0.95rem', color: '#10B981' }} />
                <span>DycoVue In-House Report</span>
                {reports.dycovueReport?.facilityReview?.status === 'flagged' ? (
                  <Chip label="Flagged" size="small" color="error" sx={{ height: 18, fontSize: '0.62rem', fontWeight: 700 }} />
                ) : reports.dycovueReport ? (
                  <Chip
                    label={reports.dycovueReport.status || 'Created'}
                    size="small"
                    color={reports.dycovueReport.status === 'approved' ? 'success' : 'default'}
                    sx={{ height: 18, fontSize: '0.62rem', fontWeight: 700 }}
                  />
                ) : null}
              </Box>
            }
          />
        </Tabs>
      </Box>

      {/* Render ONLY the active report: completely separated, never stacked */}
      {activeTab === 'self-report' && (
        <ReportTypeSection
          label="Self-Report (Clinician)"
          original={reports.selfReport}
          workingCopy={reports.selfReportWorkingCopy}
          type="self-report"
          study={study}
          onWorkingCopySaved={(r) => handleWorkingCopySaved(r, 'self-report')}
          onReportUpdated={(r) => handleReportUpdated(r, 'self-report')}
        />
      )}

      {activeTab === 'dycovue-report' && (
        <ReportTypeSection
          label="DycoVue Report (Radiologist)"
          original={reports.dycovueReport}
          workingCopy={reports.dycovueReportWorkingCopy}
          type="dycovue-report"
          study={study}
          onWorkingCopySaved={(r) => handleWorkingCopySaved(r, 'dycovue-report')}
          onReportUpdated={(r) => handleReportUpdated(r, 'dycovue-report')}
          onEditDycovueReport={onEditDycovueReport}
        />
      )}
    </Box>
  );
};

export default ReportViewer;

